import { useState, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const GEMINI_MODEL = 'gemini-2.5-flash';

async function callGemini(apiKey, systemPrompt, messages, maxTokens = 500) {
  // Build Gemini contents array from message history
  const contents = messages.map(m => ({
    role: m.role === 'assistant' ? 'model' : 'user',
    parts: [{ text: m.content }],
  }));

  const body = {
    system_instruction: { parts: [{ text: systemPrompt }] },
    contents,
    generationConfig: { maxOutputTokens: maxTokens, temperature: 0.8 },
  };

  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error?.message || `خطأ في Gemini API: ${res.status}`);
  }

  const data = await res.json();
  return data.candidates?.[0]?.content?.parts?.[0]?.text || '';
}

export function useAnthropicChat(agent) {
  const { apiKey } = useApp();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const systemPrompt = `${agent.prompt}

أنت تجري مقابلة UX باللغة العربية. اتبع هذه التعليمات:
1. اسأل سؤالاً واحداً فقط في كل رسالة
2. استمع للمستخدم وتابع إجاباته بأسئلة متعمقة
3. كن ودوداً ومحفزاً
4. المقابلة يجب أن تغطي حوالي ${agent.questionCount || 7} أسئلة
5. هدف المقابلة: ${agent.objective}
6. استخدم اللغة العربية الفصحى البسيطة أو العامية الخفيفة المفهومة
7. لا تكشف أنك ذكاء اصطناعي إلا لو سُئلت مباشرة`;

  const sendMessage = useCallback(async (userMessage, currentMessages) => {
    if (!apiKey) {
      setError('يرجى إدخال مفتاح Gemini API أولاً في إعدادات المنصة');
      return null;
    }
    setLoading(true);
    setError(null);

    const messages = [
      ...currentMessages.map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content: userMessage },
    ];

    try {
      return await callGemini(apiKey, systemPrompt, messages, 500);
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiKey, agent]);

  const analyzeInterview = useCallback(async (messages) => {
    if (!apiKey) return null;
    setLoading(true);
    setError(null);

    const transcript = messages
      .map(m => `${m.role === 'assistant' ? 'المقابل' : 'المشارك'}: ${m.content}`)
      .join('\n');

    const analysisPrompt = `أنت محلل UX خبير. حلّل نص المقابلة التالية واستخرج رؤى منظمة. أجب بـ JSON فقط بدون أي نص إضافي أو backticks.

استخرج:
- summary: ملخص عام لتجربة المستخدم (جملتين)
- painPoints: مصفوفة من { label: "اسم نقطة الألم", severity: رقم من 1 إلى 5 }
- userNeeds: مصفوفة من النصوص العربية لاحتياجات المستخدم
- opportunities: مصفوفة من فرص التحسين
- keyQuotes: مصفوفة من { text: "الاقتباس", questionIndex: رقم }
- tags: مصفوفة من الكلمات المفتاحية
- severityScore: متوسط درجة الخطورة (1-5)

نص المقابلة:
${transcript}`;

    try {
      const text = await callGemini(apiKey, analysisPrompt, [{ role: 'user', content: 'حلّل المقابلة' }], 1500);
      const clean = text.replace(/```json|```/g, '').trim();
      return JSON.parse(clean);
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiKey, agent]);

  const getInitialMessage = useCallback(async () => {
    if (!apiKey) return 'مرحباً! أنا هنا لإجراء مقابلة قصيرة معك حول تجربتك. هل أنت مستعد للبدء؟';
    setLoading(true);

    const initPrompt = `${agent.prompt}

هدف المقابلة: ${agent.objective}

ابدأ المقابلة الآن بشكل مباشر — رحّب بالمستخدم بجملة واحدة قصيرة ثم اطرح السؤال الأول مباشرة. لا تنتظر منه أن يقول "اهلا" أو "مستعد". ابدأ فوراً.`;

    try {
      return await callGemini(apiKey, initPrompt, [{ role: 'user', content: 'ابدأ المقابلة' }], 250);
    } catch {
      return `مرحباً! شاكرين مشاركتك معنا 😊 ${agent.objective ? `نبغى نسولفك عن ${agent.objective}` : ''} — متى آخر مرة استخدمت هنقرستيشن؟`;
    } finally {
      setLoading(false);
    }
  }, [apiKey, agent]);

  return { sendMessage, analyzeInterview, getInitialMessage, loading, error };
}
